import {
  Button,
  ConfirmModal,
  FilterSearchBar,
  OverflowTooltip,
  TablePaginationContext,
  UnLink,
  UserAvatar,
} from '@/components';
import { Pagination } from '@/components/TablePaginationContextProvider/Pagination';
import { SortableTable } from '@/components/TableV2/SortableTable';
import { ColumnDef } from '@/components/TableV2/TableV2';
import { ToggleColumn } from '@/components/ToggleColumn';
import { useDisassociateOrganizationUser } from '@/modules/Organizations/queries/disassociate-user';
import { OrganizationAdmin } from '@/modules/Users/domain';
import { CreateOrganizationUserModal } from '@/modules/Users/pages/UsersPage/components/CreateOrganizationUserModal/CreateOrganizationUserModal';
import { useGetAdminsByOrganization } from '@/modules/Users/queries/use-get-admins-by-organization';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type OrganizationAdminsTabProps = {
  organizationId: number;
};

export const OrganizationAdminsTab = ({
  organizationId,
}: OrganizationAdminsTabProps) => {
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number>();

  const { t } = useTranslation();
  const {
    page,
    searchTerm,
    tableSort,
    handleSearchChange,
    onSort,
    onPageChange,
  } = useContext(TablePaginationContext);
  const sort = tableSort[0];
  const { data: admins, isLoading } = useGetAdminsByOrganization({
    organizationId,
    pageNumber: page,
    filter: searchTerm,
    sortBy: sort?.columnId,
    sortOrder: sort?.direction,
  });

  const { mutate: disassociateUserOrganization } =
    useDisassociateOrganizationUser();

  const columns = useMemo<ColumnDef<OrganizationAdmin>[]>(
    () => [
      {
        id: 'name',
        name: t('name'),
        accessor: 'fullName',
        alignment: 'left',
        tdClassNames: 'max-w-[15vh]',
        renderRow: (row) => {
          return (
            <div className="flex items-center gap-2.5 overflow-hidden">
              <UserAvatar
                size="small"
                name={row.fullName}
                imageId={row.profileImageFileId}
              />
              <OverflowTooltip text={row.fullName} textClassName="font-black" />
            </div>
          );
        },
      },
      {
        id: 'email',
        name: t('email'),
        accessor: 'email',
      },
      {
        id: 'status',
        name: t('status'),
        accessor: 'status',
        renderRow: (row) => (
          <ToggleColumn onChange={() => {}} row={row} value={row.status} />
        ),
      },
      {
        id: 'access',
        name: t('access'),
        accessor: 'access',
        renderRow: (row) => (
          <ToggleColumn onChange={() => {}} row={row} value={row.access} />
        ),
      },
      {
        id: 'actions',
        name: '',
        renderRow: (row) => (
          <UnLink
            className="cursor-pointer"
            onClick={() => {
              setSelectedUserId(row.id);
            }}
          />
        ),
      },
    ],
    [t],
  );

  return (
    <>
      <div className="center flex flex-row">
        <FilterSearchBar
          total={admins?.totalRows || 0}
          title={t('administrators')}
          onSearch={handleSearchChange}
          variant={'secondary'}
          otherActions={
            <>
              <Button
                onClick={() => setIsUserModalOpen(true)}
                variant="primary"
              >
                {t('add_administrator')}
              </Button>
            </>
          }
        />
      </div>
      <SortableTable
        columns={columns}
        rows={admins?.items ?? []}
        emptyStateMessage={t('no_administrators_available')}
        isLoading={isLoading}
        loadingRows={3}
        onSort={(sorts) =>
          onSort(
            sorts.map((sort) => ({
              columnId: sort.accessor,
              direction: sort.direction,
            })),
          )
        }
      />
      <Pagination
        currentPage={page}
        onPageChange={onPageChange}
        totalPages={admins?.totalPages ?? 0}
      />
      <CreateOrganizationUserModal
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        organizationId={organizationId}
      />
      <ConfirmModal
        title="Please confirm action"
        message={t('confirm_user_organization_disassociate')}
        isOpen={!!selectedUserId}
        onConfirm={() => {
          if (selectedUserId) {
            disassociateUserOrganization({
              organizationId,
              userId: selectedUserId,
            });
          }

          setSelectedUserId(undefined);
        }}
        handleClose={() => {
          setSelectedUserId(undefined);
        }}
      />
    </>
  );
};
